import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Linkedin, Lightbulb, Settings, Users } from 'lucide-react';

const MissionPillar = ({ icon: Icon, title, body }) => {
  return (
    <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-all h-full">
      <CardContent className="p-6 text-center">
        <div className="p-3 bg-[#1A56DB]/10 rounded-full w-fit mx-auto mb-4">
          <Icon className="text-[#1A56DB]" size={24} />
        </div>
        <h3 className="text-lg font-bold text-[#0A1628] mb-2">{title}</h3>
        <p className="text-[#6B7280] text-sm leading-relaxed">{body}</p>
      </CardContent>
    </Card>
  );
};

const FounderCard = ({ name, title, bio1, bio2, linkedinUrl, photoSrc, photoClassName = 'object-center scale-100' }) => {
  return (
    <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-lg overflow-hidden">
      <CardContent className="p-0">
        <div className="flex flex-col md:flex-row">
          {/* Founder photo */}
          <div className="md:w-60 bg-[#EFF6FF] flex items-center justify-center flex-shrink-0 border-b md:border-b-0 md:border-r border-[#D1D5DB] px-6 py-8">
            <div className="w-40 h-40 md:w-44 md:h-44 rounded-full overflow-hidden border-4 border-white shadow-md bg-white">
              <img
                src={photoSrc}
                alt={`${name} headshot`}
                className={`w-full h-full object-cover transform-gpu ${photoClassName}`}
              />
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 p-6">
            <h3 className="text-2xl font-bold text-[#0A1628] mb-1">{name}</h3>
            <p className="text-[#0E7490] font-semibold mb-4">{title}</p>
            
            <div className="space-y-3 text-[#6B7280] text-sm leading-relaxed mb-6">
              <p>{bio1}</p>
              <p>{bio2}</p>
            </div>
            
            <Button
              asChild
              variant="outline"
              size="sm"
              className="border-[#1A56DB] text-[#1A56DB] hover:bg-[#1A56DB] hover:text-white transition-colors"
            >
              <a 
                href={linkedinUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2"
              >
                <Linkedin size={16} />
                LinkedIn
              </a>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const About = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Dark Navy */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-[#060D1B] via-[#0A1628] to-[#0d1f3c]">
        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="text-center" data-testid="about-header">
            {/* <div className="inline-block px-3 py-1 bg-[#1A56DB]/20 text-[#1A56DB] text-xs font-semibold uppercase tracking-wider rounded-full mb-4">
              About Cognifin
            </div> */}
            <h1 className="text-h1 text-white mb-6">
              Built by People Who Ran the System.
            </h1>
            <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto">
              Cognifin was founded by two practitioners who spent decades inside the financial institutions they are now building infrastructure for.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section - Light Background */}
      <section className="section-padding px-6 bg-[#EFF6FF]" data-testid="mission-section">
        <div className="max-w-[1200px] mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-h3 text-[#0A1628] mb-4">
              The Mission: Build the New Banking Layers.
            </h2>
            <p className="text-body-lg text-[#6B7280] leading-relaxed">
              Financial institutions sit on vast reserves of data and operational expertise. What they lack is an intelligence layer that converts that data into real-time, structured, decision-ready insight — with every action traceable, every policy configurable, and every decision firmly in human hands. Cognifin is building that layer.
            </p>
          </div>
          
          {/* Three Mission Pillars */}
          <div className="grid md:grid-cols-3 gap-6">
            <MissionPillar
              icon={Lightbulb}
              title="Intelligence Layer"
              body="From raw data to decision-ready intelligence — automated, accurate, auditable."
            />
            <MissionPillar
              icon={Settings}
              title="Configurable by Design"
              body="Every policy, every rule, every threshold — owned and configured by your team."
            />
            <MissionPillar
              icon={Users}
              title="Human at the Centre"
              body="AI prepares. Humans decide. No exceptions."
            />
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="section-padding px-6 bg-white" data-testid="leadership-section">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-h2 text-[#0A1628] mb-10 text-center">Leadership</h2>
          
          <div className="grid lg:grid-cols-2 gap-8">
            <FounderCard
              name="Gyanendra Singh"
              title="Co-founder"
              bio1="Gyan is a fintech operator with 19+ years building financial products at the intersection of credit, data, and consumer behaviour. He has led product and growth functions at Visa, OneCard, Experian, and Dobin."
              bio2="At Cognifin, Gyan drives the product vision and go-to-market strategy — translating deep domain knowledge into a platform designed to solve the real operational constraints of BFSI institutions."
              linkedinUrl="https://www.linkedin.com/in/gyanendra-singh-b8a6837/"
              photoSrc="/GS_profile.jpg"
              photoClassName="object-[40%_20%] scale-[1.18]"
            />
            
            <FounderCard
              name="Paras Pagare"
              title="Co-founder"
              bio1="Paras is an AI entrepreneur and technology leader with 25+ years of experience across global software companies and startups. He has led large-scale engineering organisations and architected mission-critical enterprise systems."
              bio2="He has held senior leadership roles at Icertis, IBM, and two venture-backed startups, and has served as CTO and Co-founder designing and scaling enterprise SaaS products from early concept to production adoption."
              linkedinUrl="https://www.linkedin.com/in/paraspagare/"
              photoSrc="/PP_profile.jpg"
              photoClassName="object-[54%_20%] scale-[1.18]"
            />
          </div>
        </div>
      </section>

      {/* Geography Section - Light Background */}
      <section className="section-padding px-6 bg-[#EFF6FF]" data-testid="geography-section">
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 className="text-h2 text-[#0A1628] mb-5">Building for Global Finance.</h2>
          <p className="text-body-lg text-[#6B7280] max-w-4xl mx-auto mb-8 leading-relaxed">
            Cognifin is headquartered in India with active engagements across South Asia and Southeast Asia. Our architecture is designed to adapt to regional regulatory frameworks — from India's Account Aggregator to Singapore's open banking rails to global bureau and document intelligence needs.
          </p>
          
          {/* Market Tag Pills */}
          <div className="flex flex-wrap justify-center gap-3">
            {['India', 'Singapore', 'Southeast Asia', 'Global BFSI'].map((market) => (
              <span 
                key={market}
                className="px-5 py-2 bg-white border border-[#D1D5DB] rounded-full text-[#374151] text-sm font-medium"
              >
                {market}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Dark Background */}
      <section className="section-padding px-6 bg-[#0A1628]" data-testid="about-cta">
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 className="text-h2 text-white mb-6">
            Want to work with us or partner with us?
          </h2>
          
          <div className="flex flex-col items-center gap-4">
            <Button 
              asChild 
              size="lg" 
              className="bg-[#1A56DB] hover:bg-[#1A56DB]/90 text-white font-bold text-lg px-10 h-14 btn-hover-lift shadow-lg shadow-[#1A56DB]/30"
            >
              <Link to="/contact">Book a Discovery Call →</Link>
            </Button>
            
            <a 
              href="mailto:info@cognifin.tech" 
              className="text-white hover:text-[#1A56DB] transition-colors font-medium"
            >
              info@cognifin.tech
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
